import { useState } from 'react';
import { Phone, Mail, MapPin, Facebook, Clock, Calendar, Instagram } from 'lucide-react';
import { Button } from '../components/ui/button';
import { AppointmentModal } from '../components/AppointmentModal';
import { GoogleReviews } from '../components/GoogleReviews';
import { NavBar } from '../components/NavBar';
import { Footer } from '../components/Footer';
import { PageHead } from '../components/PageHead';
import { StructuredData, breadcrumb } from '../components/StructuredData';
import { useShopStatus } from '../hooks/useShopStatus';
import { track } from '../utils/umami';
import '../App.css';

export function ContactPage() {
  const shopStatus = useShopStatus();
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div className="App">
      <PageHead
        title="Infos & Contact – Lunetterie du Russey | Horaires, adresse, RDV"
        description="Horaires d'ouverture, adresse et contact de la Lunetterie du Russey. 3 Rue des Rondeys, 25210 Le Russey. Tél : 03 81 67 90 20. Prise de rendez-vous en ligne."
      />
      <StructuredData data={breadcrumb([
        { name: 'Accueil', path: '/' },
        { name: 'Infos & Contact', path: '/contact' },
      ])} />
      <NavBar />

      <section id="contact" className="contact-section page-section-first wave-bottom-to-dark">
        <div className="section-container">
          <div className="section-header">
            <h1 className="section-title" style={{ color: '#f5f1e8' }}>Infos & Contact</h1>
            <p className="section-description" style={{ color: 'rgba(245,241,232,0.8)', fontWeight: 400 }}>
              Nous sommes là pour prendre soin de votre vue
            </p>
          </div>

          <div className="contact-layout">
            <div className="contact-main">
              <div className="contact-two-columns">
                {/* Horaires */}
                <div className="contact-left-column">
                  <div className={`hours-status-badge ${shopStatus.isOpen ? 'open' : 'closed'}`}>
                    <Clock className="h-4 w-4" />
                    <span className={shopStatus.isOpen ? 'status-open' : 'status-closed'}>
                      {shopStatus.isOpen ? 'Ouvert' : 'Fermé'}
                    </span>
                    {shopStatus.message && <span className="status-next">· {shopStatus.message}</span>}
                  </div>
                  <h2 className="hours-title">Les horaires</h2>
                  <div className="hours-list">
                    {[
                      ['LUNDI',    'Fermé toute la journée', true],
                      ['MARDI',    '09h00 - 12h00 · 14h00 - 19h00'],
                      ['MERCREDI', '09h00 - 12h00 · 14h00 - 19h00'],
                      ['JEUDI',    '09h00 - 12h00 · 14h00 - 19h00'],
                      ['VENDREDI', '09h00 - 12h00 · 14h00 - 19h00'],
                      ['SAMEDI',   '09h00 - 16h00'],
                      ['DIMANCHE', 'Fermé toute la journée', true],
                    ].map(([day, hours, closed]) => (
                      <div key={day} className="hour-item">
                        <span className={`day-name${closed ? ' closed' : ''}`}>{day}</span>
                        <span className="hours-time">{hours}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Carte + Coordonnées */}
                <div className="contact-right-column">
                  <div className="contact-map-wrapper">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2712.965637356424!2d6.71818387667999!3d47.15852741881132!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x478df0da1785ad2b%3A0x5bcf88fd2717f4f0!2sLUNETTERIE%20DU%20RUSSEY!5e0!3m2!1sfr!2sfr!4v1764364751080!5m2!1sfr!2sfr"
                      width="100%" height="350" style={{ border: 0 }}
                      allowFullScreen="" loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Carte – Lunetterie du Russey"
                    />
                  </div>
                  <div className="contact-details">
                    <div className="contact-detail-item">
                      <MapPin className="h-5 w-5" />
                      <div><p>3 Rue des Rondeys</p><p>25210 Le Russey</p></div>
                    </div>
                    <div className="contact-detail-item">
                      <Phone className="h-5 w-5" />
                      <a href="tel:0381679020" onClick={() => track('clic-telephone', { source: 'contact' })}>03 81 67 90 20</a>
                    </div>
                    <div className="contact-detail-item">
                      <Mail className="h-5 w-5" />
                      <a href="mailto:lunetteriedurussey@gmail.com" onClick={() => track('clic-email', { source: 'contact' })}>lunetteriedurussey@gmail.com</a>
                    </div>
                  </div>
                  <div className="contact-actions">
                    <Button className="action-button primary-action" onClick={() => { track('ouverture-modal-rdv', { source: 'contact' }); setModalOpen(true); }}>
                      <Calendar className="mr-2 h-5 w-5" />
                      Prendre rendez-vous
                    </Button>
                    <div className="social-links">
                      <a href="https://www.facebook.com/Lunetteriedurussey/" target="_blank" rel="noopener noreferrer" className="social-link" onClick={() => track('clic-reseau-social', { reseau: 'facebook', source: 'contact' })}>
                        <Facebook className="h-5 w-5" />
                      </a>
                      <a href="https://www.instagram.com/lunetteriedurussey/" target="_blank" rel="noopener noreferrer" className="social-link" onClick={() => track('clic-reseau-social', { reseau: 'instagram', source: 'contact' })}>
                        <Instagram className="h-5 w-5" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="contact-reviews-wrapper">
              <div className="contact-reviews-sidebar">
                <GoogleReviews />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <AppointmentModal open={modalOpen} onOpenChange={setModalOpen} />
    </div>
  );
}
